import { NgClass } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatBottomSheetRef } from "@angular/material/bottom-sheet";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import {
  Subject,
  Subscription,
  catchError,
  finalize,
  isObservable,
  switchMap,
  takeUntil,
  throwError,
} from "rxjs";
import { CommonVariable } from "src/app/app.constants";
import { JackpotTriggersService } from "src/app/services/jackpot-triggers.service";
import { StoreJackpotRepositoryService } from "../../store/store-jackpot.service";
import { SyndicateRepositoryService } from "../syndicate-repository.service";
interface jackpotTrigger {
  sql_trigger_id: string;
  jackpot_trigger: string;
  preferred_currency: {
    jackpot_trigger: string;
  };
}

@Component({
  selector: 'app-jackpot-change-threshold',
  templateUrl: './jackpot-change-threshold.component.html',
  styleUrls: ['./jackpot-change-threshold.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    IonicModule,
    TranslatePipe,
  ],
})
export class JackpotChangeThresholdComponent implements OnInit, OnDestroy {

  selectedLanguage: string = CommonVariable.preferredLanguage;
  public jackpotValue: string;
  public sliderMin: number;
  public sliderMax: number;
  public sliderStep: number;
  public isConfirmButtonDisabled = false;
  public isDeactivating = false;
  public userId: string;
  public triggerId: string;
  public currentThreshold: string;
  public sqlId: string;
  public currencySymbol: string;
  public preferredCurrency: string;
  public unit: string;
  public numericValue: string;
  public formattedValue: string;
  public proSubId: string;
  private userDetailsSubscription: Subscription;
  private languageSubscription: Subscription;
  private destroy$ = new Subject<void>();
  public selectedTriggerData: Data;
  public centerValue: number;
  constructor(
    private jackpotTriggerService: JackpotTriggersService,
    private syndicateService: SyndicateRepositoryService,
    private storeJackpotService: StoreJackpotRepositoryService,
    private authService: AuthService,
    public translate: TranslateService,
    private bottomSheetRef: MatBottomSheetRef<JackpotChangeThresholdComponent>,
    private router: Router
  ) {
    this.currencySymbol = "£";
    this.userDetailsSubscription = this.authService.userDetails$
    .pipe(
      switchMap((user) => {
        this.preferredCurrency = user?.preferred_currency ? user?.preferred_currency : "GBP";
        return this.authService.getCurrencyList$;
      })
    )
    .subscribe((data: Data) => {
      const CurrencyDetails = data?.details?.find(
        (item) => item?.currency_code === this.preferredCurrency
      );
  
      this.currencySymbol = CurrencyDetails?.currency_symbol
        ? CurrencyDetails?.currency_symbol
        : "£";
    });

    this.languageSubscription = this.authService?.selectedlanguage$?.subscribe(
      (l) => {
        this.selectedLanguage = l;
        translate.use(l ? l : CommonVariable?.preferredLanguage);
      },
      (err) => {
        return err;
      }
    );
  }
  async ngOnInit(): Promise<void> {
    await this.authService
      ?.getUid("JackpotChangeThresholdComponent")
      ?.then((uid) => {
        this.userId = uid;
      })
      .catch((error: any) => {
        return error;
      });
    const currentRoute = this.router.url;
    if (currentRoute === '/subscription-settings') {
      this.selectedTriggerData = this.jackpotTriggerService.shareJackpotData()?.trigger;
    } else {
      this.selectedTriggerData = this.jackpotTriggerService.shareJackpotData();
    }

    const thresholdSliderData = this.selectedTriggerData?.threshold_slider;
    this.sliderMin = thresholdSliderData?.[this.preferredCurrency]?.min ?? thresholdSliderData?.min;
    this.sliderMax = thresholdSliderData?.[this.preferredCurrency]?.max ?? thresholdSliderData?.max;
    this.sliderStep = thresholdSliderData?.[this.preferredCurrency]?.increment ?? thresholdSliderData?.increment;
    this.triggerId = this.selectedTriggerData.id;
    this.proSubId = this.selectedTriggerData.offer_link_id;

    // Center the slider if jackpotValue is not set
    if (!this.jackpotValue) {
      this.centerValue = (this.sliderMin + this.sliderMax) / 2;
    }

    this.jackpotTriggerService.getUserTriggerData(this.userId, this.triggerId).then((data: jackpotTrigger) => {
      this.currentThreshold = data.preferred_currency.jackpot_trigger;
      this.jackpotValue = data.preferred_currency.jackpot_trigger || this.jackpotValue;
      if (this.jackpotValue && typeof this.jackpotValue === "string") {
        this.unit =
          this.jackpotValue.replace(/[\d.-]/g, "") ||
          (data?.jackpot_trigger && typeof data.jackpot_trigger === "string"
            ? data.jackpot_trigger.replace(/[\d.-]/g, "")
            : "");
      } else {
        this.unit = "";
      }
      this.jackpotValue = this.jackpotValue.replace(/[^\d.-]/g, "");
      this.formattedValue = this.jackpotValue + this.unit;
      this.sqlId = data?.sql_trigger_id;
    }).catch((error) => {
      console.error("Error fetching user trigger data", error);
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.userDetailsSubscription?.unsubscribe();
    this.languageSubscription?.unsubscribe();
  }
  onSliderChange(event: CustomEvent) {
    this.centerValue = event?.detail?.value;
    this.formattedValue = this.centerValue + (this.unit || '');
  }

  onSliderRelease() {
    if (this.formattedValue === this.currentThreshold) {
      this.isConfirmButtonDisabled = true;
    } else {
      this.isConfirmButtonDisabled = false;
    }
  }


  public update(data: string) {
    this.bottomSheetRef.dismiss();
    let updateTriggerData = {
      userId: this.userId,
      updatedThreshold: this.getJackpotValue(data),
      proSubId: this.proSubId,
      offerId: this.selectedTriggerData?.offer_id,
      sqlId: this.sqlId,
    };
    this.syndicateService.updateThreshold(updateTriggerData).subscribe(
      (res) => {
        if (res?.status === "rejected") {
          this.authService.getConstantsMsg(res?.error_code).then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Something went wrong!",
                line_2: "",
              },
              style: {
                icon: "tick circle error.svg",
                type: "Error",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          }).catch((error) => {
            return error;
          });
          return;
        }
        if (res.status === "fail") {
          this.authService.getConstantsMsg("1056").then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Process failed",
                line_2: "",
              },
              style: {
                icon: "tick circle error.svg",
                type: "Error",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          }).catch((error) => {
            return error;
          });
        } else {
          this.authService.getConstantsMsg("1120").then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: `${this.selectedTriggerData?.jackpot_name}  Jackpot Trigger Threshold updated `,
                line_2: "",
              },
              style: {
                icon: "tick circle success.png",
                type: "success",
              },
            };
            translatedMsg.message.line_1 = this.selectedTriggerData?.jackpot_name + translatedMsg?.message?.line_1;
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          }).catch((error) => {
            return error;
          });
        }
      },
      (err) => {
        return err;
      }
    );
  }

  getJackpotValue(data: string): string {
    if (data === null || data === undefined) {
      return this.jackpotValue || '';
    }
    const stringData = String(data); // Explicitly convert to string
    if (!stringData || stringData.toLowerCase() === 'undefined' || stringData.toLowerCase() === 'null') {
      return this.jackpotValue || '';
    }
    return data;
  }

  public closeBox() {
    if (this.isDeactivating) {
      return;
    }

    const selectedTriggerData = {
      lines: this.selectedTriggerData?.lines,
      jackpot: this.selectedTriggerData?.jackpot,
      price: this.selectedTriggerData?.price,
      free_jackpot: this.selectedTriggerData?.free_jackpot ?? false,
      alternate_free_jackpot: this.selectedTriggerData?.alternate_free_jackpot ?? false,
      free_offer_id: this.selectedTriggerData?.free_offer_id ?? 0,
      offer_link_id:
        this.selectedTriggerData?.offer_link_id ?? this.selectedTriggerData?.prosub_id,
      offer_id: this.selectedTriggerData?.offer_id,
    };

    if (!selectedTriggerData?.offer_link_id || !selectedTriggerData?.offer_id) {
      this.authService.errorHandlerLogs(
        "[JackpotChangeThreshold] Missing ids for deactivate",
        selectedTriggerData
      );
      this.authService.getConstantsMsg("1056").then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "Process failed",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      }).catch((error) => error);
      this.bottomSheetRef.dismiss(false);
      return;
    }

    const deactivate$ = this.syndicateService.deactivateTrigger(selectedTriggerData);
    if (!isObservable(deactivate$)) {
      this.authService.errorHandlerLogs(
        "[JackpotChangeThreshold] Deactivate trigger did not return an Observable",
        deactivate$
      );
      this.authService.getConstantsMsg("1056").then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "Process failed",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      }).catch((error) => error);
      this.bottomSheetRef.dismiss(false);
      return;
    }

    this.isDeactivating = true;
    deactivate$
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.authService.errorHandlerLogs(
            "[JackpotChangeThreshold] Deactivate trigger request failed",
            error
          );
          return throwError(() => error);
        }),
        finalize(() => {
          this.isDeactivating = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res || res?.status === "rejected" || res?.status === "fail") {
            if (res?.status === "rejected") {
              this.authService.getConstantsMsg(res?.error_code).then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Something went wrong!",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                this.authService.showToastMessage(translatedMsg, defaultMsg);
              }).catch((error) => error);
            } else {
              this.authService.getConstantsMsg("1056").then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Process failed",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                this.authService.showToastMessage(translatedMsg, defaultMsg);
              }).catch((error) => error);
            }
            this.bottomSheetRef.dismiss(false);
            return;
          }

          try {
            this.storeJackpotService.markUserJackpotTriggerInactive(
              this.selectedTriggerData?.id
            );
          } catch (error) {
            this.authService.errorHandlerLogs(
              "[JackpotChangeThreshold] Failed to mark trigger inactive locally",
              error
            );
          }

          this.bottomSheetRef.dismiss(true);

          try {
            this.authService.getConstantsMsg("1065").then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: `${this.selectedTriggerData?.jackpot_name} Jackpot Trigger deactivated`,
                  line_2: "",
                },
                style: {
                  icon: "tick circle success.png",
                  type: "success",
                },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            }).catch((error) => error);
          } catch (error) {
            this.authService.errorHandlerLogs(
              "[JackpotChangeThreshold] Post-deactivate side effect failed",
              error
            );
          }
        },
        error: () => {
          this.authService.getConstantsMsg("1056").then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Process failed",
                line_2: "",
              },
              style: {
                icon: "tick circle error.svg",
                type: "Error",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          }).catch((error) => error);
          this.bottomSheetRef.dismiss(false);
        },
      });
  }
  closeDialog() {
    if (this.isDeactivating) {
      return;
    }
    this.bottomSheetRef.dismiss();
  }

}
